import { type FC } from "react";
import { Toolbar, Checkbox, Button, Typography, Divider } from "@mui/material";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import type { MutationErrorLike } from "@/partner/shared/lib";
import { ToolbarProgress, useConfirmAction } from "@/shared/components";
import {
  marketingErrorKey,
  useDeleteContactListsMutation,
} from "@/partner/entities/marketing";

interface ContactListsListToolbarProps {
  indeterminate: boolean;
  allSelected: boolean;
  numSelected: number;
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  selectedIds: string[];
  onCloseSelected: () => void;
}

/**
 * Тулбар выбранных списков: удаление, как BroadcastsListToolbar.
 * Подтверждение предупреждает, что контакты, которых нет в других
 * списках, удаляются насовсем.
 */
export const ContactListsListToolbar: FC<ContactListsListToolbarProps> = ({
  indeterminate,
  allSelected,
  numSelected,
  onSelectAllClick,
  selectedIds,
  onCloseSelected,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { confirm, dialog: confirmDialog } = useConfirmAction();
  const [deleteLists, { loading }] = useDeleteContactListsMutation();

  const onDeleteSelected = () => {
    confirm({
      title: t("marketingContacts.bulk.delete"),
      text: t("marketingContacts.bulk.confirmDelete"),
      onConfirm: () =>
        deleteLists({
          variables: { ids: selectedIds },
          onCompleted: () => {
            onCloseSelected();
            enqueueSnackbar(t("marketingContacts.bulk.deleted"), {
              variant: "success",
            });
          },
          onError: (error: MutationErrorLike) =>
            enqueueSnackbar(t(marketingErrorKey(error)), { variant: "error" }),
        }),
    });
  };

  return (
    <Toolbar sx={{ position: "relative" }}>
      <Checkbox
        color="default"
        indeterminate={indeterminate}
        checked={allSelected}
        onChange={onSelectAllClick}
        sx={{ marginLeft: "-16px", marginRight: { xs: 0, sm: "16px" } }}
      />
      <Typography>
        {t("common.toolbar.selectedCount", { count: numSelected })}
      </Typography>
      <Divider
        orientation="vertical"
        variant="middle"
        sx={{ mx: 1.25, height: "2em" }}
      />
      <Button
        color="inherit"
        onClick={onDeleteSelected}
        disabled={loading || numSelected === 0}
      >
        {t("marketingContacts.bulk.delete")}
      </Button>
      <ToolbarProgress loading={loading} />
      {confirmDialog}
    </Toolbar>
  );
};
